/** ~meters between two WGS84 points (haversine). */
export function haversineMeters(
  a: { lat: number; lng: number },
  b: { lat: number; lng: number }
): number {
  const R = 6371000;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const lat1 = (a.lat * Math.PI) / 180;
  const lat2 = (b.lat * Math.PI) / 180;
  const x =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(x)));
}

/**
 * Append the live bus position to the GPS trail when it is far enough from the last
 * breadcrumb (Firestore may lag behind the marker).
 */
export function appendLiveLocationToPath(
  path: { lat: number; lng: number; gapBefore?: boolean; estimated?: boolean }[],
  live: { lat: number; lng: number } | null | undefined,
  minSeparationM = 45
): { lat: number; lng: number; gapBefore?: boolean; estimated?: boolean }[] {
  if (!live || !Number.isFinite(live.lat) || !Number.isFinite(live.lng)) return path;
  if (path.length === 0) return [live];
  const last = path[path.length - 1];
  if (haversineMeters(last, live) < minSeparationM) return path;
  return [...path, live];
}

/** Max GPS samples above which we assume the trail is dense enough without road snapping. */
export const FLEET_MAP_SNAP_MAX_POINTS = 28;

/**
 * Best point to show a bus on the map: prefer live fix, else last breadcrumb (same as driver trail).
 * Avoids empty maps when `liveLocation` is temporarily null but `routePoints` exists.
 */
export function bestPositionForActiveBus(b: {
  liveLocation: { lat: number; lng: number } | null | undefined;
  gpsRoutePoints?: { lat: number; lng: number }[] | null;
}): { lat: number; lng: number } | null {
  const live = b.liveLocation;
  if (
    live &&
    Number.isFinite(live.lat) &&
    Number.isFinite(live.lng)
  ) {
    return live;
  }
  const pts = b.gpsRoutePoints ?? [];
  if (!pts.length) return null;
  const last = pts[pts.length - 1];
  if (last && Number.isFinite(last.lat) && Number.isFinite(last.lng)) return last;
  return null;
}
